import { AxiosResponse } from 'axios';

import {
  ICreateDriverShiftRequest,
  ICreateMultipleDriverShiftsResponse,
  IDeleteDriverShiftResponse,
  IDeleteDriverShiftsFilters,
  IDriverShiftEditScopeOptions,
  IDriverShiftsQueryRequestMonthlyFilters,
  IGetDriverShiftDailyResponse,
  IGetDriverShiftMonthlyResponse,
  IGetDriverShiftWeeklyResponse,
  IGetDriverShiftsDailyFilters,
  IGetDriverShiftsFilters,
  IGetDriverShiftsResponse,
  IGetDriverShiftsWeeklyFilters,
  IGetSignleDriverShiftResponse,
  IUdateDriverShiftResponse,
  IUpdateDriverShiftRequest,
} from 'models/http/driverShift';
import { IGetHistoryResponse } from 'models/http/history';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from 'utils/http';

import { HTTP } from './core.http';
import DataUploadBaseHttp from './utils/data-upload.http';

class DriverShiftHttp extends DataUploadBaseHttp implements HTTP<IDeleteDriverShiftsFilters> {
  public static DataExportRoute = `${ModelsNames.DriverShifts.replace(/_/g, '-')}/data-export`;
  protected readonly route: DataUploadModels = ModelsNames.DriverShifts;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.wireRoute}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async get(filters?: IGetDriverShiftsFilters, abortController?: AbortController): Promise<IGetDriverShiftsResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.wireRoute}?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftsResponse>) => {
        return response.data;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSignleDriverShiftResponse> {
    return serverAxios
      .get(`/${this.wireRoute}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSignleDriverShiftResponse>) => {
        return response.data;
      });
  }

  public async getDay(filters: IGetDriverShiftsDailyFilters, abortController?: AbortController): Promise<IGetDriverShiftDailyResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.wireRoute}/calendar/daily?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftDailyResponse>) => {
        return response.data;
      });
  }

  public async getWeek(filters: IGetDriverShiftsWeeklyFilters, abortController?: AbortController): Promise<IGetDriverShiftWeeklyResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`${this.wireRoute}/calendar/weekly?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftWeeklyResponse>) => {
        return response.data;
      });
  }

  public async getMonth(
    filters: IDriverShiftsQueryRequestMonthlyFilters,
    abortController?: AbortController,
  ): Promise<IGetDriverShiftMonthlyResponse> {
    const query = prepareQPFromFilters(filters);

    return serverAxios
      .get(`/${this.wireRoute}/calendar/monthly?${query.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftMonthlyResponse>) => {
        return response.data;
      });
  }

  public async createMany(data: ICreateDriverShiftRequest): Promise<ICreateMultipleDriverShiftsResponse> {
    return serverAxios
      .post(`/${this.wireRoute}`, {
        ...data,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateMultipleDriverShiftsResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, data: IUpdateDriverShiftRequest): Promise<IUdateDriverShiftResponse> {
    return serverAxios
      .put(`/${this.wireRoute}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUdateDriverShiftResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[], editScope?: IDriverShiftEditScopeOptions): Promise<IDeleteDriverShiftResponse>;
  public async delete(filters: IDeleteDriverShiftsFilters, editScope?: IDriverShiftEditScopeOptions): Promise<IDeleteDriverShiftResponse>;
  public async delete(
    filters: number[] | IDeleteDriverShiftsFilters,
    editScope?: IDriverShiftEditScopeOptions,
  ): Promise<IDeleteDriverShiftResponse> {
    return serverAxios
      .delete(`/${this.wireRoute}/`, {
        // The series edit/delete scope (single / thisAndFuture / allEvents) rides alongside the selection.
        data: { ...prepareFiltersOfSelection(filters), ...editScope },
      })
      .then((response: AxiosResponse<IDeleteDriverShiftResponse>) => {
        return response.data;
      });
  }
}

export default DriverShiftHttp;
